import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Symbol } from "../GameComponents/Symbol";
import { ModuleManager } from "../Managers/ModuleManager";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { Tweens } from "../Components/Effects/Tweens";

/**
 * Reel2 handles:
 * - z-order
 * - usage of Normal instead of Rotation
 */
@engineComponent(ComponentTypes.DisplayObject)
export class Reel2 extends Reel {
    public static Tease: boolean = false;

    protected overlayReel: BaseElement;

    public addOverlayReel(overlayReel: BaseElement): void {
        this.overlayReel = overlayReel;
    }

    public showSymbols(useTransform: boolean = false): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];

        this.x = this.positionX;
        this.y = this.positionY;
        this.overlayReel.x = this.positionX;
        this.overlayReel.y = this.positionY;

        if (!useTransform) {
            while (this.currentSymbols.length > 0) {
                this.returnSymbol(this.currentSymbols.pop());
            }
        }

        let bottomSymbols: Array<Symbol> = [];
        let topSymbols: Array<Symbol> = [];

        for (let i = 0; i < this.symbolBottomCount + this.symbolCount + this.symbolTopCount; i++) {
            let symbolId: number;
            let symbolCase: string;

            if (i < this.symbolBottomCount) {
                if (ModuleManager.freespinsActive() && (this.index === 0 || this.index === 4)) {
                    symbolId = 11;
                } else {
                    symbolId = this.getOuterSymbol(i);
                }
                symbolCase = i === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (i < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - i - 1];
                symbolCase = Symbol.CaseRotation;
            } else {
                if (ModuleManager.freespinsActive() && (this.index === 0 || this.index === 4)) {
                    symbolId = 11;
                } else {
                    symbolId = this.getOuterSymbol(i);
                }
                symbolCase = i === this.symbolBottomCount + this.symbolCount ? Symbol.CaseRotation : Symbol.CaseBlur;
            }

            if (!useTransform) {
                // when we don't want use symbols from WinAnimator
                this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, false));
            } else {
                // currentSymbols are used from WinAnimator. We only need to add new top and bottom symbols
                if (i < this.symbolBottomCount) {
                    bottomSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, false));
                } else if (i > this.symbolBottomCount + this.symbolCount - 1) {
                    topSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, false));
                } else if (this.currentSymbols[this.symbolCount - i] == null) {
                    //add symbol below expanded wild
                    this.currentSymbols[this.symbolCount - i] = this.getSymbol(symbolId - 1, symbolCase, false);
                }
            }
        }

        if (useTransform) {
            while (bottomSymbols.length > 0) {
                this.currentSymbols.push(bottomSymbols.shift());
            }

            while (topSymbols.length > 0) {
                this.currentSymbols.unshift(topSymbols.shift());
            }
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            this.addChild(this.currentSymbols[i]);

            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        if (this.data.overlay) {
            for (let i = 0; i < this.symbolCount; i++) {
                if (this.data.overlay[i] !== null && this.data.overlay[i] > 0) {
                    this.currentSymbols[i + this.symbolTopCount].goToCase(Symbol.CaseEmpty);
                }
            }
        }

        this.moveToTop();
    }

    protected updateReel(): void {
        this.returnSymbol(this.currentSymbols.pop());

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if (this.previousReel.isStopping) {
                this.isStopping = true;
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = Symbol.CaseBlur;
        let fromStrip: boolean = true;

        if (this.isStopping) {
            if (this.stoppingCounter < this.symbolBottomCount) {
                if (ModuleManager.freespinsActive() && (this.index === 0 || this.index === 4)) {
                    symbolId = 11;
                } else {
                    symbolId = this.getOuterSymbol(this.stoppingCounter);
                }
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                if (
                    this.data.overlay !== undefined &&
                    this.data.overlay[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1] > 0
                ) {
                    if (
                        this.data.transform !== undefined &&
                        this.data.transform[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1] > 0
                    ) {
                        symbolCase = Symbol.CaseRotation;
                    } else {
                        symbolCase = Symbol.CaseEmpty;
                    }
                } else {
                    symbolCase = Symbol.CaseRotation;
                }
                fromStrip = false;
            } else {
                if (ModuleManager.freespinsActive() && (this.index === 0 || this.index === 4)) {
                    symbolId = 11;
                } else {
                    symbolId = this.getOuterSymbol(this.stoppingCounter);
                }
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? Symbol.CaseRotation
                        : Symbol.CaseBlur;
                fromStrip = false;
            }

            this.stoppingCounter++;
        }

        this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, fromStrip));

        this.addChild(this.currentSymbols[0]);

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
        this.moveToTop();
    }

    private moveToTop(): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];

            if (symbol !== null && symbol.id === 10) {
                this.overlayReel.addChild(symbol);
            }
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];

            if (symbol !== null && symbol.id === 11) {
                this.overlayReel.addChild(symbol);
            }
        }
    }

    protected spinPreRoll(): void {
        // super.spinPreRoll();
        Tweens.removeTweens(this, "position");
        let tween = Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY - this.preRoll, this.positionY],
            tweenDuration: this.preRollSpeed * 2,
            tweenDelay: this.startDelay,
            callbackFunction: this.spinRoll,
            callbackScope: this,
        });

        tween.eventCallback("onUpdate", this.updateOverlayReel.bind(this));
    }

    protected spinRoll(): void {
        // super.spinRoll();
        Tweens.removeTweens(this, "position");
        let tween = Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + this.symbolHeight],
            tweenDuration: this.rollSpeed,
            callbackFunction: this.updateReel,
            callbackScope: this,
        });

        tween.eventCallback("onUpdate", this.updateOverlayReel.bind(this));
    }

    protected spinPostRoll(): void {
        // super.spinPostRoll();
        Tweens.removeTweens(this, "position");
        let tween = Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + this.postRoll, this.positionY],
            tweenDuration: this.postRollSpeed * 2,
            callbackFunction: this.spinComplete,
            callbackScope: this,
        });

        tween.eventCallback("onUpdate", this.updateOverlayReel.bind(this));
    }

    private updateOverlayReel(): void {
        this.overlayReel.y = this.y;
    }

    protected getSymbol(symbolId: number, symbolCase: string, fromStrip: boolean): Symbol {
        if (symbolCase === Symbol.CaseRotation) {
            symbolCase = Symbol.CaseNormal;
        }
        if (Reel2.Tease && symbolId !== 11) {
            symbolCase = Symbol.CaseNowin;
        }

        const symbol = super.getSymbol(symbolId, symbolCase, fromStrip);
        // if(Reel2.Tease && symbolId !== 10) {
        //     symbol.alpha = 0.7;
        // }
        return symbol;
    }

    protected getOuterSymbol(index: number): number {
        let ruleData = {} as ReelRuleData;
        ruleData.index = index;
        ruleData.strip = this.strip;
        ruleData.stripIndex =
            (this.data.index + this.symbolBottomCount + this.symbolCount - index - 1 + this.strip.length) %
            this.strip.length;
        ruleData.symbolId = ruleData.strip[ruleData.stripIndex];

        const reelRules = [this.singleSpecialSymbol.bind(this), this.checkOuterSymbol.bind(this)];
        for (let i = 0; i < reelRules.length; i++) {
            ruleData = reelRules[i](ruleData);
        }

        return ruleData.symbolId;
    }

    //rules
    protected singleSpecialSymbol(ruleData: ReelRuleData): ReelRuleData {
        const specialSymbols = [12];

        if (this.data != null) {
            if (this.hasSpecialSymbol(this.data.symbols, specialSymbols)) {
                ruleData.strip = ruleData.strip.filter((value) => {
                    return specialSymbols.indexOf(value) == -1;
                });

                if (this.data.symbols.indexOf(ruleData.symbolId) > -1) {
                    ruleData.stripIndex = (ruleData.stripIndex + ruleData.strip.length) % ruleData.strip.length;
                    ruleData.symbolId = ruleData.strip[ruleData.stripIndex];
                }
            }
        }

        return ruleData;
    }

    protected hasSpecialSymbol(currentSymbols: Array<number>, specialSymbols: Array<number>): boolean {
        for (let i = 0; i < specialSymbols.length; i++) {
            if (currentSymbols.indexOf(specialSymbols[i]) > -1) {
                return true;
            }
        }
        return false;
    }

    protected checkOuterSymbol(ruleData: ReelRuleData): ReelRuleData {
        if (ruleData.index == this.symbolBottomCount - 1) {
            ruleData.symbolId = this.getNextOuterSymbol(ruleData, this.data.symbols[this.data.symbols.length - 1], 1); //bottom symbols check
        } else if (ruleData.index == this.symbolBottomCount + this.symbolCount) {
            ruleData.symbolId = this.getNextOuterSymbol(ruleData, this.data.symbols[0], -1); //top symbols check
        }

        return ruleData;
    }

    protected getNextOuterSymbol(ruleData: ReelRuleData, edgeSymbol: number, offset: number): number {
        const specialSymbols = [12];

        //if bottom/top symbol is same like edge symbol
        if (ruleData.symbolId == edgeSymbol) {
            let anotherEdge = offset == 1 ? this.data.symbols[0] : this.data.symbols[this.data.symbols.length - 1];
            if (ruleData.symbolId == anotherEdge) {
                const strip = ruleData.strip.filter((value) => {
                    return value !== edgeSymbol;
                });
                const index = (ruleData.stripIndex + strip.length) % strip.length;
                return strip[index];
            } else {
                return ruleData.symbolId;
            }
        } else {
            let anotherEdge = offset == 1 ? this.data.symbols[0] : this.data.symbols[this.data.symbols.length - 1];
            //also be careful about special symbol
            if (edgeSymbol == anotherEdge || specialSymbols.indexOf(edgeSymbol) > -1) {
                return ruleData.symbolId;
            } else {
                return edgeSymbol;
            }
        }
    }
}

export type ReelRuleData = {
    symbolId: number;
    index: number;
    stripIndex: number;
    strip: Array<number>;
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/Reel2.ts
 **/